import type {
  AgentInstance,
  AgentFile,
  AgentListing,
  AgentType,
  AdminErrorClusterDetails,
  AdminErrorScanResult,
  AdminErrorSummary,
  AdminOverview,
  AdminBalanceUpdateResponse,
  Approval,
  AuthResult,
  BillingPlansResponse,
  PairingCode,
  CronJob,
  CronSchedule,
  LocalTask,
  LocalTaskMessage,
  MeResponse,
  PurchaseEmailCodeStartResponse,
  PurchaseVerifyResponse,
  PublicBillingPlansResponse,
  ResearchProject,
  ResearchProjectDetail,
  ResearchPlanning,
  ResearchQuestion,
  ResearchDocument,
  ResearchEvidenceSpan,
  ResearchClaimAudit,
  ResearchClaimVerdict,
  ResearchRun,
  ResearchSearch,
  ResearchSearchIteration,
  ResearchSource,
  RegisterPayload,
  SessionEvent,
  SessionOutput,
  SessionRecord,
  Tokens,
  UploadedFile
} from "../types/api";

export type SessionTarget =
  | { executionTarget: "cloud_hermes"; agentInstanceId: string }
  | { executionTarget: "local_device"; deviceId: string; agent?: AgentType };

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function parse(response: Response): Promise<any> {
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.error
      ? typeof json.error === "string"
        ? json.error
        : JSON.stringify(json.error)
      : `HTTP ${response.status}`;
    throw new ApiError(message, response.status);
  }
  return json;
}

// --- Pre-login auth calls (no access token) -------------------------------
export async function register(baseUrl: string, payload: RegisterPayload): Promise<AuthResult> {
  const response = await fetch(`${baseUrl}/v1/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  return parse(response);
}

export async function login(baseUrl: string, email: string, password: string): Promise<AuthResult> {
  const response = await fetch(`${baseUrl}/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  return parse(response);
}

export async function requestRegisterEmailCode(baseUrl: string, email: string, inviteCode?: string): Promise<void> {
  const response = await fetch(`${baseUrl}/v1/auth/register/email-code/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(inviteCode ? { email, inviteCode } : { email })
  });
  await parse(response);
}

export async function requestLoginEmailCode(baseUrl: string, email: string): Promise<void> {
  const response = await fetch(`${baseUrl}/v1/auth/login/email-code/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email })
  });
  await parse(response);
}

export async function loginWithEmailCode(baseUrl: string, email: string, emailCode: string): Promise<AuthResult> {
  const response = await fetch(`${baseUrl}/v1/auth/login/email-code/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, emailCode })
  });
  return parse(response);
}

export async function requestPurchaseEmailCode(baseUrl: string, email: string): Promise<PurchaseEmailCodeStartResponse> {
  const response = await fetch(`${baseUrl}/v1/auth/purchase/email-code/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email })
  });
  return parse(response);
}

export async function verifyPurchaseEmailCode(
  baseUrl: string,
  email: string,
  emailCode: string,
  password?: string
): Promise<PurchaseVerifyResponse> {
  const response = await fetch(`${baseUrl}/v1/auth/purchase/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, emailCode, ...(password ? { password } : {}) })
  });
  return parse(response);
}

export async function refreshTokens(baseUrl: string, refreshToken: string): Promise<{ tokens: Tokens }> {
  const response = await fetch(`${baseUrl}/v1/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken })
  });
  return parse(response);
}

export async function logout(baseUrl: string, refreshToken: string): Promise<void> {
  await fetch(`${baseUrl}/v1/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken })
  }).catch(() => undefined);
}

export async function getPublicBillingPlans(baseUrl: string): Promise<PublicBillingPlansResponse> {
  const response = await fetch(`${baseUrl}/v1/billing/public-plans`);
  return parse(response);
}

// --- Authenticated client -------------------------------------------------
export interface ApiClientDeps {
  baseUrl: string;
  getAccess: () => string;
  getRefresh: () => string;
  onTokens: (tokens: Tokens) => Promise<void>;
  onUnauthorized: () => void;
}

export class ApiClient {
  private refreshInFlight: Promise<void> | null = null;
  private unauthorizedNotified = false;

  constructor(private readonly deps: ApiClientDeps) {}

  get baseUrl(): string {
    return this.deps.baseUrl;
  }

  /**
   * Access tokens expire for every visible pane at the same time. Collapse the
   * resulting 401 burst into one refresh because refresh tokens rotate: sending
   * the old refresh token twice makes the second request look like a logout.
   */
  private refreshAccess(): Promise<void> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = (async () => {
        const { tokens } = await refreshTokens(this.deps.baseUrl, this.deps.getRefresh());
        await this.deps.onTokens(tokens);
      })().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async prepareAuthenticatedRetry(accessToken: string): Promise<void> {
    try {
      // A sibling request may have completed the refresh before this 401 was
      // delivered. Only the request that still observes its original access
      // token joins/starts the single refresh operation.
      if (this.deps.getAccess() === accessToken) await this.refreshAccess();
    } catch (error) {
      // Only an explicit rejection of the refresh credential proves that the
      // session is over. Network failures and server 5xx responses must remain
      // retryable and must not erase a valid local login.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        if (!this.unauthorizedNotified) {
          this.unauthorizedNotified = true;
          this.deps.onUnauthorized();
        }
        throw new ApiError("session expired", 401);
      }
      throw error;
    }
  }

  private async request(path: string, init: RequestInit = {}, retry = true): Promise<any> {
    const accessToken = this.deps.getAccess();
    const response = await fetch(`${this.deps.baseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {})
      }
    });

    if (response.status === 401 && retry) {
      await this.prepareAuthenticatedRetry(accessToken);
      return this.request(path, init, false);
    }
    return parse(response);
  }

  requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.request(path, init);
  }

  me(): Promise<MeResponse> {
    return this.request("/v1/me");
  }

  // --- Billing (Stripe top-up) ---------------------------------------------
  getBillingPlans(): Promise<BillingPlansResponse> {
    return this.request("/v1/billing/plans");
  }

  createPaymentIntent(planId: string): Promise<{ clientSecret: string; paymentIntentId: string }> {
    return this.request("/v1/billing/payment-intents", {
      method: "POST",
      body: JSON.stringify({ planId })
    });
  }

  listAgents(accountId: string): Promise<AgentListing> {
    return this.request(`/v1/accounts/${accountId}/agents`);
  }

  adminOverview(): Promise<AdminOverview> {
    return this.request("/v1/admin/overview");
  }

  adminAddUserBalance(userId: string, amountUsd: number): Promise<AdminBalanceUpdateResponse> {
    return this.request(`/v1/admin/users/${encodeURIComponent(userId)}/quota`, {
      method: "PATCH",
      body: JSON.stringify({ amountUsd })
    });
  }

  adminErrorSummary(): Promise<AdminErrorSummary> {
    return this.request("/v1/admin/errors/summary");
  }

  adminErrorClusters(input: { status?: string | null; limit?: number | null } = {}): Promise<{ clusters: AdminErrorSummary["latest"] }> {
    const params = new URLSearchParams();
    if (input.status) params.set("status", input.status);
    if (input.limit) params.set("limit", String(input.limit));
    const query = params.toString();
    return this.request(`/v1/admin/errors/clusters${query ? `?${query}` : ""}`);
  }

  adminErrorCluster(fingerprint: string): Promise<AdminErrorClusterDetails> {
    return this.request(`/v1/admin/errors/clusters/${encodeURIComponent(fingerprint)}`);
  }

  adminErrorBacktest(input: { sinceIso?: string | null; untilIso?: string | null; limit?: number | null; includeStale?: boolean } = {}): Promise<AdminErrorScanResult> {
    return this.request("/v1/admin/errors/backtest", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  adminErrorScan(input: { sinceIso?: string | null; untilIso?: string | null; limit?: number | null; includeStale?: boolean; writeBugBundles?: boolean } = {}): Promise<AdminErrorScanResult> {
    return this.request("/v1/admin/errors/scan", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  adminErrorValidate(fingerprint: string): Promise<{ validation: AdminErrorClusterDetails["validations"][number] }> {
    return this.request(`/v1/admin/errors/clusters/${encodeURIComponent(fingerprint)}/validate`, { method: "POST" });
  }

  adminErrorBugBundle(fingerprint: string): Promise<{ path: string }> {
    return this.request(`/v1/admin/errors/clusters/${encodeURIComponent(fingerprint)}/bug-bundle`, { method: "POST" });
  }

  adminErrorAutofix(fingerprint: string, mode = "draft_pr"): Promise<{ run: AdminErrorClusterDetails["autofixRuns"][number] }> {
    return this.request(`/v1/admin/errors/clusters/${encodeURIComponent(fingerprint)}/autofix`, {
      method: "POST",
      body: JSON.stringify({ mode })
    });
  }

  // Recent tasks on a paired device, fetched live over the relay.
  listDeviceTasks(accountId: string, deviceId: string, sinceDays = 14): Promise<{ tasks: LocalTask[] }> {
    return this.request(`/v1/accounts/${accountId}/devices/${deviceId}/tasks?sinceDays=${sinceDays}`);
  }

  // Transcript of one local task on a paired device, fetched live over the relay.
  getDeviceTaskHistory(accountId: string, deviceId: string, detailKey: string): Promise<{ messages: LocalTaskMessage[] }> {
    return this.request(
      `/v1/accounts/${accountId}/devices/${deviceId}/tasks/history?detailKey=${encodeURIComponent(detailKey)}`
    );
  }

  // --- Local devices (pairing, revoke, approvals) --------------------------

  // Mint a short-lived pairing code to type into the desktop router (mole).
  createPairingCode(accountId: string): Promise<PairingCode> {
    return this.request(`/v1/accounts/${accountId}/pairing-codes`, { method: "POST" });
  }

  revokeDevice(accountId: string, deviceId: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/accounts/${accountId}/devices/${deviceId}/revoke`, { method: "POST" });
  }

  listApprovals(accountId: string): Promise<{ approvals: Approval[] }> {
    return this.request(`/v1/accounts/${accountId}/approvals`);
  }

  approveApproval(accountId: string, approvalId: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/accounts/${accountId}/approvals/${approvalId}/approve`, { method: "POST" });
  }

  rejectApproval(accountId: string, approvalId: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/accounts/${accountId}/approvals/${approvalId}/reject`, { method: "POST" });
  }

  /**
   * Update the cloud agent's config (rotate the OpenAI key and/or change codex
   * model settings) and re-provision. Only the provided fields change. The agent
   * instance re-enters `provisioning`; poll its status for completion.
   */
  updateAgentConfig(
    accountId: string,
    config: { openaiApiKey: string; codexBaseUrl?: string; codexModel?: string; codexReasoningEffort?: string }
  ): Promise<{ agentInstance: AgentInstance }> {
    return this.request(`/v1/accounts/${accountId}/agent-config`, {
      method: "PUT",
      body: JSON.stringify(config)
    });
  }

  // --- Cron jobs (scheduled prompts) ---------------------------------------

  listCronJobs(accountId: string): Promise<{ jobs: CronJob[] }> {
    return this.request(`/v1/accounts/${accountId}/cron-jobs`);
  }

  createCronJob(
    accountId: string,
    input: { name: string; prompt: string; schedule: CronSchedule }
  ): Promise<{ job: CronJob }> {
    return this.request(`/v1/accounts/${accountId}/cron-jobs`, {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  setCronJobEnabled(accountId: string, jobId: string, enabled: boolean): Promise<{ job: CronJob }> {
    return this.request(`/v1/accounts/${accountId}/cron-jobs/${jobId}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled })
    });
  }

  deleteCronJob(accountId: string, jobId: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/accounts/${accountId}/cron-jobs/${jobId}`, { method: "DELETE" });
  }

  /**
   * Upload a file into a cloud Hermes agent's workspace. Returns the path the
   * agent can open inside its container. Sends raw bytes (not JSON), so it has
   * its own one-shot 401 refresh rather than going through `request`.
   */
  async uploadFile(
    accountId: string,
    agentInstanceId: string,
    file: File,
    retry = true
  ): Promise<UploadedFile> {
    const accessToken = this.deps.getAccess();
    const response = await fetch(
      `${this.deps.baseUrl}/v1/accounts/${accountId}/agent-instances/${agentInstanceId}/files`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "x-filename": encodeURIComponent(file.name),
          Authorization: `Bearer ${accessToken}`
        },
        body: file
      }
    );
    if (response.status === 401 && retry) {
      await this.prepareAuthenticatedRetry(accessToken);
      return this.uploadFile(accountId, agentInstanceId, file, false);
    }
    return parse(response);
  }

  listAgentFiles(accountId: string, agentInstanceId: string): Promise<{ files: AgentFile[] }> {
    return this.request(`/v1/accounts/${accountId}/agent-instances/${agentInstanceId}/files`);
  }

  async downloadAgentFile(
    accountId: string,
    agentInstanceId: string,
    path: string,
    retry = true
  ): Promise<Blob> {
    const url = new URL(`${this.deps.baseUrl}/v1/accounts/${accountId}/agent-instances/${agentInstanceId}/files/download`);
    url.searchParams.set("path", path);
    const accessToken = this.deps.getAccess();
    const response = await fetch(url.toString(), {
      headers: { Authorization: "Bearer " + accessToken }
    });
    if (response.status === 401 && retry) {
      await this.prepareAuthenticatedRetry(accessToken);
      return this.downloadAgentFile(accountId, agentInstanceId, path, false);
    }
    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      throw new ApiError(typeof json?.error === "string" ? json.error : `HTTP ${response.status}`, response.status);
    }
    return response.blob();
  }

  async downloadChannelMessageFile(
    accountId: string,
    channelId: string,
    messageId: string,
    path: string,
    retry = true
  ): Promise<Blob> {
    const url = new URL(
      `${this.deps.baseUrl}/v1/accounts/${accountId}/channels/${channelId}/messages/${messageId}/files/download`
    );
    url.searchParams.set("path", path);
    const accessToken = this.deps.getAccess();
    const response = await fetch(url.toString(), {
      headers: { Authorization: "Bearer " + accessToken }
    });
    if (response.status === 401 && retry) {
      await this.prepareAuthenticatedRetry(accessToken);
      return this.downloadChannelMessageFile(accountId, channelId, messageId, path, false);
    }
    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      throw new ApiError(typeof json?.error === "string" ? json.error : `HTTP ${response.status}`, response.status);
    }
    return response.blob();
  }

  startSession(
    accountId: string,
    target: SessionTarget,
    prompt: string,
    conversationId?: string | null,
    // Local task / device-side thread to continue (local_device targets only).
    resumeTaskId?: string | null
  ): Promise<{ session: SessionRecord }> {
    return this.request(`/v1/accounts/${accountId}/sessions`, {
      method: "POST",
      body: JSON.stringify({
        ...target,
        prompt,
        ...(conversationId ? { conversationId } : {}),
        ...(resumeTaskId ? { resumeTaskId } : {})
      })
    });
  }

  listSessions(accountId: string): Promise<{ sessions: SessionRecord[] }> {
    return this.request(`/v1/accounts/${accountId}/sessions`);
  }

  getSession(accountId: string, sessionId: string): Promise<{ session: SessionRecord; events: SessionEvent[]; output?: SessionOutput | null }> {
    return this.request(`/v1/accounts/${accountId}/sessions/${sessionId}`);
  }

  // One page of a conversation's turns (oldest first within the page), each with
  // its events. Defaults server-side to the latest 5 turns; pass `before` (the
  // oldest loaded session id) to page older turns. `hasMore` says whether more
  // older turns remain.
  getConversation(
    accountId: string,
    conversationId: string,
    opts?: { limit?: number; before?: string | null }
  ): Promise<{ turns: Array<{ session: SessionRecord; events: SessionEvent[]; output?: SessionOutput | null }>; hasMore: boolean }> {
    const qs = new URLSearchParams();
    if (opts?.limit != null) qs.set("limit", String(opts.limit));
    if (opts?.before) qs.set("before", opts.before);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.request(`/v1/accounts/${accountId}/conversations/${conversationId}${suffix}`);
  }

  cancelSession(accountId: string, sessionId: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/accounts/${accountId}/sessions/${sessionId}/cancel`, { method: "POST" });
  }

  listResearchProjects(accountId: string, archived = false): Promise<{ projects: ResearchProject[] }> {
    return this.request(`/v1/accounts/${accountId}/research-projects${archived ? "?archived=true" : ""}`);
  }

  createResearchProject(accountId: string, topic: string, title?: string): Promise<{ project: ResearchProject; questions: ResearchQuestion[]; planning: ResearchPlanning | null }> {
    return this.request(`/v1/accounts/${accountId}/research-projects`, {
      method: "POST", body: JSON.stringify({ topic, ...(title?.trim() ? { title: title.trim() } : {}) })
    });
  }

  getResearchProject(accountId: string, projectId: string): Promise<ResearchProjectDetail> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}`);
  }

  updateResearchProject(accountId: string, projectId: string, patch: { title?: string; archived?: boolean }): Promise<{ project: ResearchProject }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}`, { method: "PATCH", body: JSON.stringify(patch) });
  }

  deleteResearchProject(accountId: string, projectId: string): Promise<void> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}`, { method: "DELETE" });
  }

  approveResearchBrief(accountId: string, projectId: string, answers: Array<string | null>, brief?: string): Promise<{ project: ResearchProject; questions: ResearchQuestion[]; brief: string | null; planning: ResearchPlanning | null }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/brief`, {
      method: "PUT", body: JSON.stringify({ answers, ...(brief?.trim() ? { brief: brief.trim() } : {}) })
    });
  }

  answerResearchQuestion(accountId: string, projectId: string, questionId: string, answer: string): Promise<{ project: ResearchProject; questions: ResearchQuestion[]; brief?: string | null; planning: ResearchPlanning | null }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/questions/${questionId}/answer`, {
      method: "POST", body: JSON.stringify({ answer })
    });
  }

  replaceResearchQuestion(accountId: string, projectId: string, questionId: string): Promise<{ question: ResearchQuestion; planning: ResearchPlanning | null; feedbackCaptured: boolean }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/questions/${questionId}/replace`, { method: "POST" });
  }

  reviewResearchSource(accountId: string, projectId: string, sourceId: string, reviewStatus: ResearchSource["reviewStatus"]): Promise<{ source: ResearchSource }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/sources/${sourceId}`, { method: "PATCH", body: JSON.stringify({ reviewStatus }) });
  }

  ingestResearchFullText(accountId: string, projectId: string, sourceId: string): Promise<{ document: ResearchDocument; evidenceSpans: ResearchEvidenceSpan[] }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/sources/${sourceId}/full-text`, { method: "POST" });
  }

  auditResearchClaims(accountId: string, projectId: string): Promise<{ claimAudits: ResearchClaimAudit[] }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/claim-audit`, { method: "POST" });
  }

  reviewResearchClaim(accountId: string, projectId: string, claimId: string, verdict: Exclude<ResearchClaimVerdict, "uncited">): Promise<{ claimAudit: ResearchClaimAudit }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/claim-audit/${claimId}`, { method: "PATCH", body: JSON.stringify({ verdict }) });
  }

  async downloadResearchFullText(accountId: string, projectId: string, sourceId: string): Promise<Blob> {
    const response = await fetch(`${this.deps.baseUrl}/v1/accounts/${accountId}/research-projects/${projectId}/sources/${sourceId}/full-text.pdf`, { headers: { Authorization: `Bearer ${this.deps.getAccess()}` } });
    if (!response.ok) { const json = await response.json().catch(() => ({})); throw new ApiError(typeof json?.error === "string" ? json.error : `HTTP ${response.status}`, response.status); }
    return response.blob();
  }

  startResearchRun(accountId: string, projectId: string, prompt?: string): Promise<{ run: ResearchRun; session: SessionRecord; sources: ResearchSource[]; search: ResearchSearch; searchIterations: ResearchSearchIteration[] }> {
    return this.request(`/v1/accounts/${accountId}/research-projects/${projectId}/runs`, { method: "POST", ...(prompt?.trim() ? { body: JSON.stringify({ prompt: prompt.trim() }) } : {}) });
  }

  async uploadResearchPdf(accountId: string, projectId: string, file: File): Promise<void> {
    const response = await fetch(`${this.deps.baseUrl}/v1/accounts/${accountId}/research-projects/${projectId}/files`, {
      method: "POST", headers: { Authorization: `Bearer ${this.deps.getAccess()}`, "Content-Type": "application/pdf", "x-filename": encodeURIComponent(file.name) }, body: file
    });
    await parse(response);
  }

  async downloadResearchReport(accountId: string, projectId: string): Promise<Blob> {
    const response = await fetch(`${this.deps.baseUrl}/v1/accounts/${accountId}/research-projects/${projectId}/report.md`, {
      headers: { Authorization: `Bearer ${this.deps.getAccess()}` }
    });
    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      throw new ApiError(typeof json?.error === "string" ? json.error : `HTTP ${response.status}`, response.status);
    }
    return response.blob();
  }

  accountEventsUrl(accountId: string, afterEventId: number): string {
    const url = new URL(this.deps.baseUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `/v1/accounts/${accountId}/events`;
    url.searchParams.set("token", this.deps.getAccess());
    url.searchParams.set("afterEventId", String(afterEventId));
    return url.toString();
  }

  sessionStreamUrl(accountId: string, sessionId: string, afterEventId: number): string {
    const url = new URL(this.deps.baseUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `/v1/accounts/${accountId}/sessions/${sessionId}/stream`;
    url.searchParams.set("token", this.deps.getAccess());
    url.searchParams.set("afterEventId", String(afterEventId));
    return url.toString();
  }

  workspaceStreamUrl(accountId: string): string {
    const url = new URL(this.deps.baseUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `/v1/accounts/${accountId}/workspace/stream`;
    url.searchParams.set("token", this.deps.getAccess());
    return url.toString();
  }
}
